/**
 * hpq-flavor-overlay.js
 * HPQ: Mounts the 麻/辣/鲜 flavor sticker on the active PDP gallery slide.
 *
 * Data source: #hpq-flavor-overlay-data (rendered by snippets/hpq-flavor-overlay.liquid)
 * Mount target: first .product__media-container .product__media-item with offsetWidth > 0
 * Position: absolute left:16px; bottom:16px; z-index:100; pointer-events:none
 *
 * Rebinds on slide change via MutationObserver on .swiper-slide-active.
 * Does nothing if all three spice values are 0.
 */

(function () {
  'use strict';

  var OVERLAY_ID = 'hpq-flavor-overlay-sticker';
  var DATA_EL_ID = 'hpq-flavor-overlay-data';

  // Dot palette for the frosted overlay:
  //   Filled → brand ink #1e140f (honest across all 3 dimensions — 麻/辣/鲜
  //   aren't all heat signals, so red read as universal spicy-alert)
  //   Empty  → pure white. On a frosted surface (55% alpha) over colored
  //   product images, grey disappears into the surface; white pops
  //   regardless of the underlying photo tone.
  var FILLED_COLOR = '#1e140f';
  var EMPTY_COLOR  = '#ffffff';

  // ── Read spice data from Liquid-rendered JSON island ──────────────────────

  function readData() {
    var el = document.getElementById(DATA_EL_ID);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  // ── Build the sticker HTML string ─────────────────────────────────────────

  function dots(filled, total) {
    var html = '';
    for (var i = 1; i <= total; i++) {
      var color = i <= filled ? FILLED_COLOR : EMPTY_COLOR;
      html += '<span style="color:' + color + ';font-size:13px;line-height:1;letter-spacing:2px;">●</span>';
    }
    return html;
  }

  function buildSticker(data) {
    var rows = [
      { char: '麻', name: 'tingly', val: data.mala },
      { char: '辣', name: 'spicy',  val: data.heat  },
      { char: '鲜', name: 'umami',  val: data.umami },
    ];

    var gridRows = rows.map(function (row) {
      var val = Math.max(0, Math.min(5, Number(row.val) || 0));
      return (
        '<div style="display:flex;align-items:baseline;gap:8px;white-space:nowrap;">' +
          '<span style="font-family:\'Noto Sans SC\',sans-serif;font-weight:500;font-size:15px;line-height:1;color:#1e140f;opacity:0.75;">' + row.char + '</span>' +
          '<span style="font-family:\'IBM Plex Mono\',monospace;font-weight:500;font-size:11px;letter-spacing:0.04em;line-height:1;color:#1e140f;">' + row.name + '</span>' +
        '</div>' +
        '<div style="display:flex;align-items:center;gap:3px;">' +
          dots(val, 5) +
        '</div>'
      );
    }).join('');

    var div = document.createElement('div');
    div.id = OVERLAY_ID;
    div.setAttribute('aria-hidden', 'true');
    // Frosted-glass surface — reuses the HPQ product-card badge pattern
    // (rgba(255,255,255,0.55) + backdrop-filter blur+saturate). No shadow,
    // no hard border. The offset-shadow "sticker" look was parked for v2.
    div.style.cssText = [
      'position:absolute',
      'left:16px',
      'bottom:16px',
      'z-index:100',
      'pointer-events:none',
      'background:rgba(255,255,255,0.55)',
      'backdrop-filter:blur(12px) saturate(1.4)',
      '-webkit-backdrop-filter:blur(12px) saturate(1.4)',
      'color:#1e140f',
      'border:none',
      'border-radius:10px',
      'box-shadow:none',
      'padding:14px 16px',
      'display:grid',
      'grid-template-columns:auto 1fr',
      'gap:6px 12px',
      'align-items:center',
      'min-width:0',
    ].join(';');

    div.innerHTML = gridRows;
    return div;
  }

  // ── Mount / re-mount the sticker ──────────────────────────────────────────
  // Attach to the gallery CONTAINER (not the active slide) so the sticker
  // stays visible across slide changes without needing a rebind observer.

  function findVisibleSlide() {
    // Mobile carousel: the swiper sets `.swiper-slide-active` on the slide
    // currently centered in the viewport. ALL swiper slides have non-zero
    // offsetWidth (swiper uses transforms, not display:none, to scroll the
    // strip), so a "first non-zero offsetWidth" sweep would mount the
    // sticker on the FIRST slide regardless of which one is on screen,
    // making the sticker invisible until the user scrolled to slide 0.
    // Prefer the explicitly-active swiper slide.
    var active = document.querySelector('.product__media-item.swiper-slide-active');
    if (active && active.offsetWidth > 0) return active;

    // Desktop / non-carousel mobile: Stiletto toggles `.hidden` to show one
    // slide at a time. First non-hidden, non-zero-width item wins.
    var items = document.querySelectorAll('.product__media-item');
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      if (it.offsetWidth > 0 && !it.classList.contains('hidden')) return it;
    }
    return null;
  }

  function mount(data) {
    var old = document.getElementById(OVERLAY_ID);
    if (old) old.parentNode.removeChild(old);

    var host = findVisibleSlide();
    if (!host) return;

    var currentPos = window.getComputedStyle(host).position;
    if (currentPos === 'static') {
      host.style.position = 'relative';
    }

    host.appendChild(buildSticker(data));
  }

  function observeSlideChanges(data) {
    // Rebind when the currently-visible slide changes. Stiletto renders TWO
    // gallery containers on PDP: `.product__media-container.above-mobile`
    // (desktop, toggles `.hidden`) and a separate mobile swiper container
    // (toggles `.swiper-slide-active`). Observing only the first match meant
    // mobile swipes never triggered remount and the sticker disappeared
    // when the active slide changed. Observe every matching container.
    var containers = document.querySelectorAll('.product__media-container');
    if (!containers.length) return;
    var observer = new MutationObserver(function () {
      var currentHost = document.getElementById(OVERLAY_ID)?.parentElement;
      var visible = findVisibleSlide();
      if (visible && visible !== currentHost) mount(data);
    });
    containers.forEach(function (c) {
      observer.observe(c, {
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style'],
      });
    });
  }

  // ── Entry point ───────────────────────────────────────────────────────────

  function init() {
    var data = readData();
    if (!data) return;

    // Gate: skip if all three values are 0
    if (!data.mala && !data.heat && !data.umami) return;

    mount(data);
    observeSlideChanges(data);

    // Rebind on variant/flavor swap (hpq-pdp.js replaces the section DOM)
    document.addEventListener('hpq:product:swapped', function () {
      var fresh = readData();
      if (fresh) mount(fresh);
    });

    // Rebind on resize so the sticker follows the visible slide when
    // switching between desktop and mobile gallery layouts.
    var resizeTimer = null;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () { mount(data); }, 150);
    });
  }

  // Run after DOM + deferred scripts are ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
